<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="FAQ-title"
            class="required"
          >
            <b-form-input
              id="FAQ-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="FAQ-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.writer')"
            label-for="faq-writer"
            class="required"
            :state="validateState('writer')"
          >
            <v-select-custom
              id="faq-writer"
              v-model="form.writer"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'writer' }, form.lang)"
              @search="fetchUsersByRole({role: 'writer', 'search' : $event}, form.lang)"
            />
            <b-form-invalid
              id="input-writerId-feedback"
              :label="$t('general.writer')"
              :validator="$v.form.writer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="faq-editor"
          >
            <v-select-custom
              id="faq-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="userOptions"
              @open="fetchUsersByRole({ role: 'editor' }, form.lang)"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="faq-category"
            class="required"
            :state="validateState('category')"
          >
            <v-select-custom
              id="faq-category"
              v-model="form.category"
              :options="FAQCategories"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="FAQ-keywords"
            class="required mb-2"
          >
            <b-form-input
              id="FAQ-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col cols="12">
          <b-form-group
            :label="$t('general.question')"
            label-for="FAQ-add-FAQ"
          >
            <BFormTextarea
              id="FAQ-FAQ"
              v-model="form.question"
              rows="5"
              :state="validateState('question')"
            />
            <b-form-invalid
              id="input-question-feedback"
              :label="$t('general.question')"
              :validator="$v.form.question"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="faq-answer"
            class="mb-2"
          >
            <CkeditorBasic
              id="faq-answer"
              v-model="form.answer"
              :label="$t('general.answer')"
              :state="validateState('answer')"
            />
            <b-form-invalid
              id="input-answer-feedback"
              :label="$t('general.answer')"
              :validator="$v.form.answer"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="mom-form-actions"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            :aria-busy="isSubmit ? 'true' : 'false'"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            :to="{ name: 'pages-FAQs-list' }"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
    <!--/ form -->
  </b-card>
</template>
<script>
import {
  BButton, BCard, BCol, BForm, BFormGroup, BFormInput, BFormTextarea, BRow,
} from 'bootstrap-vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import languageSelector from '@/components/language-selector.vue'
import { required, minLength } from 'vuelidate/lib/validators'
import focusFirstInvalid from '@/composable/focusFirstInvalid'
import useUnsavedChangesGuard, { useFormBaseline } from '@/composable/useUnsavedChangesGuard'
import useFAQsList from './useFAQsList'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations: {
    form: {
      title: {
        required,
        minLength: minLength(3),
      },
      question: {
        required,
      },
      answer: {
        required,
      },
      category: {
        required,
      },
      keywords: {
        required,
        minLength,
      },
      writer: {
        required,
      },
    },
  },
  setup() {
    const {
      FAQCategories,
      addItem,
      errorManager,
      fetchFAQCategories,
      fetchUsersByRole,
      form,
      isSubmit,
      itemLoading,
      userOptions,
    } = useFAQsList()
    const { capture: captureBaseline, isDirty, adoptEditorNormalization } = useFormBaseline(form)
    useUnsavedChangesGuard(isDirty)
    return {
      captureBaseline,
      adoptEditorNormalization,
      FAQCategories,
      addItem,
      errorManager,
      fetchFAQCategories,
      fetchUsersByRole,
      form,
      isSubmit,
      itemLoading,
      userOptions,
    }
  },
  data() {
    return {
      writerOptions: [],
      editorOptions: [],
    }
  },
  created() {
    this.$watch(() => this.form.answer, () => this.adoptEditorNormalization('answer'))
    this.form.lang = 'fa'
    this.fetchUsersByRole({
      role: 'writer',
    }).then(res => {
      this.writerOptions = res
    })
    this.fetchUsersByRole({
      role: 'editor',
    }).then(res => {
      this.editorOptions = res
    })
    this.fetchFAQCategories('fa')
    this.captureBaseline()
  },
  methods: {
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      if (this.isSubmit) return
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        this.$nextTick(() => focusFirstInvalid(this.$el))
        return
      }
      const data = {
        title: this.form.title,
        question: this.form.question,
        answer: this.form.answer,
        faqCategoryId: this.form.category ? this.form.category._id : null,
        keywords: this.form.keywords,
        writerId: this.form.writer ? this.form.writer._id : null,
        editorId: this.form.editor ? this.form.editor._id : null,
      }
      this.addItem(data, this.form.lang)
    },
    searchCategories(search) {
      this.fetchFAQCategories(this.form.lang, search)
    },
    changeSelectedLanguage(selectedLanguage) {
      this.fetchFAQCategories(selectedLanguage)
    },
  },
}
</script>
